import type { RequestInit } from '@vercel/fetch'
import { graphqlFetch } from '@pylot-data/graphqlFetch'
import {
    ConfigurableProduct,
    GiftCardProduct,
    BundleProduct
} from '@pylot-data/fwrdschema'
import {
    productDetailQuery,
    productsBySkuQuery,
    productConfigurableBySkuQuery
} from '@pylot-data/hooks/product/graphql/productDetailQuery'
import type { SearchCriteria } from '@corratech/pylot-filters-and-sort'
import { SearchCriteriaFilterActions } from '@corratech/pylot-filters-and-sort'
import type { ProductInterface } from '@pylot-data/pylotschema'
import { isLikelyMagentoSku } from '@pylot-data/hooks/search/utils/isSearchConfigurableCandidate'

export type ProductVariablesInterface = {
    currentPage?: number
    pageSize?: number
    searchCriteria: SearchCriteria[]
}

export type OutputInterface = {
    productDetail: {
        items: ConfigurableProduct[] & GiftCardProduct[] & BundleProduct[]
    }
}

interface ProductsBySkuInput {
    skus: string[]
    pageSize: number
}

interface ProductsBySkuOutput {
    products: {
        items: ProductInterface[]
    }
}

export async function getProductsBySku(
    product_skus: string[],
    locale: string,
    fetchOptions?: RequestInit
): Promise<ProductsBySkuOutput> {
    try {
        const productsData = await graphqlFetch<
            ProductsBySkuInput,
            ProductsBySkuOutput
        >({
            query: productsBySkuQuery,
            variables: {
                skus: product_skus,
                // Magento devuelve 20 si no se pide el tamaño. El resto de
                // SKUs existe, pero la home los muestra como no disponibles.
                pageSize: Math.max(product_skus.length, 1)
            },
            // POST avoids very long GET URLs (query + variables); GET can fail or hang on some gateways.
            fetchOptions: { method: 'POST', ...fetchOptions },
            locale
        })

        return productsData.data
    } catch (e) {
        throw new Error('An error occurred while fetching products')
    }
}

const configurableProductCache: Record<string, ProductInterface> = {}
const configurableProductMissCache = new Set<string>()

/**
 * Batch-fetches configurable_options/variants for SRP (PLP-parity).
 * Uses the same session cache as getProductConfigurableBySku.
 */
export async function getProductsConfigurableBySkus(
    skus: string[],
    locale: string,
    fetchOptions?: RequestInit
): Promise<Record<string, ProductInterface>> {
    const uniqueSkus = Array.from(
        new Set(skus.filter((sku): sku is string => isLikelyMagentoSku(sku)))
    )
    const result: Record<string, ProductInterface> = {}
    const missingSkus: string[] = []

    for (const sku of uniqueSkus) {
        const cached = configurableProductCache[sku]
        if (cached) {
            result[sku] = cached
        } else if (!configurableProductMissCache.has(sku)) {
            missingSkus.push(sku)
        }
    }

    if (missingSkus.length === 0) {
        return result
    }

    try {
        const productsData = await graphqlFetch<
            ProductsBySkuInput,
            ProductsBySkuOutput
        >({
            query: productConfigurableBySkuQuery,
            variables: { skus: missingSkus },
            fetchOptions: { method: 'POST', ...fetchOptions },
            locale
        })

        const foundSkus = new Set<string>()
        for (const item of productsData.data?.products?.items ?? []) {
            if (item?.sku) {
                configurableProductCache[item.sku] = item
                result[item.sku] = item
                foundSkus.add(item.sku)
            }
        }

        for (const sku of missingSkus) {
            if (!foundSkus.has(sku)) {
                configurableProductMissCache.add(sku)
            }
        }

        return result
    } catch (e) {
        throw new Error('An error occurred while fetching configurable product')
    }
}

/**
 * Fetches a product with configurable_options/variants for SRP quick-add.
 * Results are cached per SKU for the session.
 */
export async function getProductConfigurableBySku(
    sku: string,
    locale: string,
    fetchOptions?: RequestInit
): Promise<ProductInterface | null> {
    const bySku = await getProductsConfigurableBySkus(
        [sku],
        locale,
        fetchOptions
    )
    return bySku[sku] ?? null
}

async function getProduct(
    product_url: string,
    locale: string,
    fetchOptions?: RequestInit
): Promise<OutputInterface> {
    const productData = await graphqlFetch<
        ProductVariablesInterface,
        OutputInterface
    >({
        query: productDetailQuery,
        variables: {
            searchCriteria: [
                {
                    attribute_code: 'url_key',
                    filter_action: SearchCriteriaFilterActions.EQ,
                    filter_value: product_url
                }
            ]
        },
        fetchOptions,
        locale
    })

    return productData.data
}

export default getProduct
